import { z } from "zod/v4";
import { supportedCountries } from "./countries.ts";

const datetimeNotes = [
  'Format should be MMM-DD HH:MM e.g. "oct-10 14:00" means October 10th at 2pm.',
  "do not use any other format!!! Never append Z or UTC offsets - always emit local wall-clock time in the user's timezone.",
  "Remember that 00:00 is the start of the day, 23:59 is the end of the day.",
  "If someone gives a date, they probably mean morning to night, so don't start at 00:00 but at 8am or something like that.",
  "Starting from 00:00 will include events that started the previous day but are still ongoing past midnight — this is intentional, as they are still active events. If you only want events that BEGIN on a specific day, use onlyStartedInRange=true.",
  "If you mean the whole day, omit the hours.",
].join("\n");

export const zodAiTimeQuery = z.object({
  holiday: z.string().optional().describe(
    "holiday names mentioned, e.g. valentine, purim, independence day, sylvester. You must include this if the user mentioned a holiday.",
  ),
  startDatetime: z.string().describe(datetimeNotes),
  endDatetime: z.string().optional().describe(
    `${datetimeNotes} If omitted, defaults to exactly one week after startDatetime.`,
  ),
});

const onlyCopy =
  "DO NOT INFER, only copy strings user has literally said in original language.";

export const aiQuery = z.object({
  limit: z.number().optional().describe(
    "Maximum number of events to return. Defaults to 10 (which matches the recommended display size). For admin users, you can set this to 0 or -1 to get ALL matching events without limit (exhaustive results).",
  ),
  skip: z.number().optional(),
  time: zodAiTimeQuery,
  free: z.boolean().optional().describe(
    "If user is looking for free events. If not specified, assume user is looking for all events.",
  ),
  venues: z.array(z.string()).optional().describe(
    "If user mentioned a specific venue name. " + onlyCopy,
  ),
  locations: z.array(z.string()).optional().describe(
    "Array of cities or regions to search across in a single query (e.g. ['תל אביב', 'ירושלים']). Use this when the user is open to multiple areas instead of querying each location separately.",
  ),
  location: z.string().optional().describe(
    onlyCopy +
      ". Don't put holiday names here. One location only. A city name or broad region/district (e.g. 'מרכז', 'Center', 'North', 'צפון', 'South', 'דרום', 'Sharon') is fine. If user mentioned a venue name, do not put location here. Use the venues field instead. If user mentioned multiple locations, use the locations array instead.",
  ),
  country: z.enum(supportedCountries as [string, ...string[]])
    .optional()
    .describe("Country to filter events by."),
  radiusKm: z.number().optional(),
  negativeLocationQuery: z.boolean().optional().describe(
    "user wanted events NOT in location",
  ),
  performers: z.array(z.string()).optional().describe(
    "Array of performer/DJ/band/artist names to search for (OR relationship - any match is fine). Always include multiple names or bilingual translations/aliases here in a single query (e.g. ['סועאד מאסי', 'Souad Massi']) instead of calling the query tool multiple times.",
  ),
  performer: z.string().optional().describe(
    "If user mentioned a performer/dj/band name. Prefer using performers array if there are multiple or if you want to include both English and local language spellings.",
  ),
  production: z.string().optional().describe(
    "If user mentioned a production or party line.",
  ),
  sexualityEvents: z.boolean().optional(),
  categories: z.array(z.string()).optional().describe([
    "OR categories: any of these is fine. Use when user is fine with either type.",
    "Examples: user says 'techno or trance', 'film or talks', 'any party or concert'.",
    "Do NOT place mandatory must-have categories here.",
  ].join("\n")),
  intersectionCategories: z.array(z.string()).optional().describe([
    "AND categories: user explicitly requires ALL of these simultaneously.",
    "Examples: 'gay techno parties' => ['gay','techno','party'] (if each clearly required together).",
    "Keep this list short; omit if user didn't clearly demand conjunction.",
  ].join("\n")),
  negativeCategories: z.array(z.string()).optional().describe(
    "what events the user is NOT looking for. Only use values the user has literally said. If you set this, do NOT remove it later just because the query returned no results.",
  ),
  texts: z.array(z.string()).optional().describe(
    "Free text queries over event description, name/title, and original submit text (OR relationship - any match is fine). When searching for terms in multiple languages or synonyms, ALWAYS pass all of them in this array in ONE single query (e.g. ['אקסטטיק', 'ecstatic dance']) instead of issuing multiple sequential queries.",
  ),
  onlyStartedInRange: z.boolean().optional().describe(
    "If true, include only events whose start time is within the requested range. If false or omitted, include all events active during the range, including ongoing events that started earlier.",
  ),
  userId: z.string().optional(),
  coordinates: z.array(z.number()).min(2).max(2).optional(),
  minAge: z.number().optional().describe(
    "Filter events by maximum allowed minimum age restriction. Set this only if user explicitly requested a specific minimum age or specified their age. E.g. if they say 'parties for 18+', set to 18; if they are 21 years old and want suitable parties, set to 21.",
  ),
  secondHandTickets: z.enum(["buy", "sell", "all"]).optional().describe(
    "Filter events to only those with active second-hand ticket listings. 'sell' = events with people selling second-hand tickets. 'buy' = events with people looking to buy second-hand tickets. 'all' = events with any second-hand ticket activity.",
  ),
  wheelchairAccessible: z.boolean().optional().describe(
    "Filter to only wheelchair accessible events (נגיש לכיסא גלגלים / נגישות). Set this when user explicitly asks for accessible events or wheelchair access.",
  ),
});
